import { useState } from 'react'
import { Link } from '@inertiajs/react'
import SeoHead from '../../components/SeoHead'
import MarketingFooter from '../../components/MarketingFooter'

const SIGNUP_HREF = '/users/sign_up?plan=solo&src=managing-multiple-loans'

const SCALE_COLUMNS = [
  {
    label: '1 loan',
    headline: 'You remember everything.',
    body: 'A spreadsheet is overkill.',
  },
  {
    label: '3–5 loans',
    headline: 'You start forgetting.',
    body:
      "You're forgetting payment dates. You miss late fees. You can't quickly answer \"how much principal is out?\"",
  },
  {
    label: '10+ loans',
    headline: 'Sunday night is reconciliation night.',
    body:
      "You're spending Sunday nights reconciling. Borrower questions take an hour. Tax season is a week-long panic.",
  },
]

const WEEKLY_STEPS = [
  {
    title: 'Glance the dashboard.',
    body: 'Anything overdue? Flagged in red.',
  },
  {
    title: 'Confirm payments came in.',
    body: 'Mark them recorded.',
  },
  {
    title: 'Check upcoming maturities.',
    body: 'Anyone need a payoff quote this week?',
  },
  {
    title: 'Check guardrails.',
    body: 'Concentration alerts? LTV drift?',
  },
  {
    title: 'Done.',
    body: 'Close the tab. Go back to sourcing deals.',
  },
]

const FEATURES = [
  {
    title: 'Portfolio dashboard.',
    body: 'Every loan, every payment, every deadline — in one view.',
  },
  {
    title: 'Automated reminders.',
    body: "Borrowers get nudged before they're late. You don't.",
  },
  {
    title: 'Concentration guardrails.',
    body:
      '"You have 60% of capital with one borrower" — the kind of thing you only notice when it\'s too late, except now you don\'t.',
  },
]

const FAQS = [
  {
    q: 'How many loans does Solo support?',
    a: 'Solo handles up to 5 active loans. Pro goes up to 25. Fund is unlimited. Switching tiers takes one click and prorates instantly.',
  },
  {
    q: "What's the difference between Solo and Pro?",
    a: 'Solo gives you the dashboard, amortization engine, payment tracking, and spreadsheet import. Pro adds 1098 generation, AI deal memos, AI risk narratives, branded PDF statements, automated email reminders, and accountant-ready tax exports (.pdf, .csv, .qbo).',
  },
  {
    q: 'Is there a per-loan fee?',
    a: 'No. Flat pricing. Solo is $29/mo regardless of whether you track 1 loan or 5. Same for Pro and Fund within their limits.',
  },
  {
    q: 'Can I track historical loans too?',
    a: "Yes. Closed and paid-off loans stay in your record for tax history and portfolio math. They don't count against your active-loan limit.",
  },
]

const SCHEMA = {
  '@graph': [
    {
      '@type': 'HowTo',
      name: 'Manage a private lending portfolio in 10 minutes a week',
      description:
        "A five-step weekly check-in for solo private lenders with portfolios of 3 to 25 loans.",
      step: WEEKLY_STEPS.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.body,
      })),
    },
    {
      '@type': 'FinancialProduct',
      name: 'LendSolo',
      description:
        'Portfolio management for solo private lenders tracking 1 to 25 private loans.',
      url: 'https://lendsolo.com/managing-multiple-loans',
      feesAndCommissionsSpecification: 'Solo $29/mo · Pro $49/mo · Fund $99/mo',
      provider: { '@type': 'Organization', name: 'LendSolo', url: 'https://lendsolo.com' },
    },
  ],
}

export default function ManagingMultipleLoans() {
  return (
    <>
      <SeoHead
        title="Managing Multiple Private Loans: A System That Scales — LendSolo"
        description="Tracking 3, 5, or 15 private loans? See how solo lenders manage portfolios in 10 minutes a week instead of every weekend."
        canonicalPath="/managing-multiple-loans"
        schema={SCHEMA}
      />

      <div className="min-h-screen" style={{ backgroundColor: '#f8f7f4' }}>
        <Header />
        <Hero />
        <ScaleFramework />
        <WeeklyCheckIn />
        <FeatureTrio />
        <FounderVoice />
        <PricingStrip />
        <FaqSection />
        <FinalCta />
        <MarketingFooter />
      </div>
    </>
  )
}

function Header() {
  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-[#e2e0d8] sticky top-0 z-40">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#0f1a2e' }}
          >
            <span className="text-[#34d399] font-bold text-sm font-mono">LS</span>
          </div>
          <span className="text-lg font-bold text-[#0f1a2e] font-display">LendSolo</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            href="/"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Product
          </Link>
          <Link
            href="/blog"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Blog
          </Link>
          <Link
            href="/users/sign_in"
            className="text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Sign In
          </Link>
          <Link
            href={SIGNUP_HREF}
            className="text-sm font-semibold text-white bg-[#0f1a2e] hover:-translate-y-0.5 hover:shadow-lg transition-all px-4 py-2 rounded-lg"
          >
            Get Started
          </Link>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-20 sm:pt-28 pb-20 sm:pb-24">
      <div
        className="absolute top-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(26,122,76,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-5">
          <span className="w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
          Portfolios of 1–25 loans
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] text-[#0f1a2e] max-w-[820px] mx-auto tracking-tight">
          10 minutes a week. <em className="italic text-[#1a7a4c]">Not every weekend.</em>
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#5a6578] max-w-[640px] mx-auto">
          When your private lending portfolio grows past 3 loans, manual tracking stops working. LendSolo replaces every spreadsheet, calendar reminder, and bank-statement download with one weekly check-in.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link
            href={SIGNUP_HREF}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Start your free trial
          </Link>
          <a
            href="#weekly-check-in"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-[#0f1a2e] rounded-[10px] text-[15px] font-semibold border-[1.5px] border-[#e2e0d8] hover:border-[#5a6578] hover:bg-white transition-all"
          >
            See the weekly check-in ↓
          </a>
        </div>
      </div>
    </section>
  )
}

function ScaleFramework() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          What changes at scale
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[820px] mb-12">
          What changes when you go from 1 loan to 5 to 15.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SCALE_COLUMNS.map((col) => (
            <div
              key={col.label}
              className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-[14px] p-7"
            >
              <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
                {col.label}
              </p>
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {col.headline}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{col.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function WeeklyCheckIn() {
  return (
    <section
      id="weekly-check-in"
      className="py-20 sm:py-24 scroll-mt-20"
      style={{ backgroundColor: '#f8f7f4' }}
    >
      <div className="max-w-[820px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          The weekly check-in
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-6">
          Your new Monday morning, in 10 minutes.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] mb-12 max-w-[640px]">
          Five steps. That's the whole routine. The dashboard does the rest of the work during the week.
        </p>
        <ol className="space-y-5">
          {WEEKLY_STEPS.map((step, i) => (
            <li
              key={step.title}
              className="bg-white border border-[#e2e0d8] rounded-[14px] p-6 flex items-start gap-4"
            >
              <span className="font-display text-2xl text-[#1a7a4c] min-w-[36px]">
                0{i + 1}
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-[#0f1a2e]">
                  {step.title}
                </h3>
                <p className="mt-1 text-[15px] leading-relaxed text-[#5a6578]">
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function FeatureTrio() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Built for scale
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[780px] mb-12">
          Three capabilities that matter past three loans.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-[14px] p-7"
            >
              <div className="font-mono text-xs text-[#1a7a4c] mb-3">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {f.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FounderVoice() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[820px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Founder
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-6">
          Built by a private lender, not a software company.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] mb-4">
          I run a small portfolio myself. The scaling problem — three loans becoming five becoming a dozen — is exactly what I built LendSolo to solve.
        </p>
        <p className="text-sm font-semibold text-[#0f1a2e]">— Pete, founder</p>
      </div>
    </section>
  )
}

function PricingStrip() {
  const tiers = [
    { name: 'Solo', price: '$29', note: 'Up to 5 loans' },
    { name: 'Pro', price: '$49', note: 'Up to 25 loans' },
    { name: 'Fund', price: '$99', note: 'Unlimited + investors' },
  ]
  return (
    <section className="py-16 sm:py-20 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <div
          className="border border-[#e2e0d8] rounded-[14px] p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          style={{ backgroundColor: '#f8f7f4' }}
        >
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-2">
              Pricing
            </p>
            <p className="font-display text-xl sm:text-2xl text-[#0f1a2e]">
              Flat monthly pricing.{' '}
              <span className="text-[#5a6578] font-normal">Annual = 2 months free.</span>
            </p>
          </div>
          <div className="flex gap-3 sm:gap-5">
            {tiers.map((t) => (
              <div key={t.name} className="flex flex-col">
                <span className="text-xs font-mono uppercase tracking-wider text-[#8c95a6]">
                  {t.name}
                </span>
                <span className="font-display text-2xl text-[#0f1a2e]">
                  {t.price}
                  <span className="text-sm text-[#5a6578]">/mo</span>
                </span>
                <span className="text-xs text-[#8c95a6]">{t.note}</span>
              </div>
            ))}
          </div>
          <Link
            href="/#pricing"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0f1a2e] border-[1.5px] border-[#e2e0d8] rounded-lg hover:border-[#5a6578] transition-all"
          >
            Full pricing →
          </Link>
        </div>
      </div>
    </section>
  )
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[720px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          FAQ
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-10">
          Common questions
        </h2>
        <div className="divide-y divide-[#e2e0d8]">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i
            return (
              <div key={item.q} className="py-6">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="text-base font-semibold text-[#0f1a2e] pr-4">
                    {item.q}
                  </span>
                  <span className="text-xl font-light text-[#8c95a6] min-w-[24px] text-center">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[500px] pt-4 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-[15px] leading-relaxed text-[#5a6578]">{item.a}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Ready?
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[720px] mx-auto mb-4">
          Built for portfolios of 1–25 loans. Try Solo free.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mx-auto mb-9">
          14-day trial, no credit card. Upgrade to Pro when you cross 5 loans.
        </p>
        <Link
          href={SIGNUP_HREF}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          Start your free trial
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8h10m0 0L9 4m4 4L9 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </section>
  )
}
